<template>
	<div
		class="overview box box--primary"
		:class="{ 'clickable': clickable }"
		:style="{ 'pointer-events': ((hasPrevNumber || !!info) ? 'auto' : '') }"
		@click="onClick"> <!-- pointer-events: auto, allow tooltip showing when overview is active -->
		<div
			class="overview__number"
			:class="numberClass">
			<div class="spinner-border" role="status"  v-if="isFetchingstatistic">
				<span class="sr-only">Loading...</span>
			</div>
			<div class="d-sm-flex d-flex-column justify-content-between" v-if="hasPrevNumber && statsEnabled">
				<div class="text-ellipsis" v-if="!isFetchingstatistic">
					<span v-tooltip="`Current Value : ${numberDisplay}`">{{ numberDisplay }}</span>
				</div>
				<div class="text-ellipsis text-right" v-if="!isFetchingstatistic">
					<small class="text-bold" :class="`${prevNumberDisplay.textColor}`" v-tooltip="`${hasPrevNumber ? `Previous Value : ${prevNumber}` : 0}`">{{ prevNumberDisplay.percentage }}</small>
				</div>
			</div>
			<div v-else>
				<span v-if="!isFetchingstatistic">
					{{ numberDisplay }}
				</span>
			</div>
		</div>
		<div class="overview__label">
			<div class="row">
				<div :class="{
					'col-md-12' : !isAdmin || !isCanRedirected,
					'col-md-8' : isAdmin,
					}" class="col-md-8 mr-0 pr-0">
					<div class="overview__label__name">
						<span
							class="overview__icon icon"
							:class="iconClass"
							v-if="icon && iconType === 'bootstrap'">
							<i :class="'bi '+icon"></i>
						</span>
						<span
							class="overview__icon icon"
							:class="iconClass"
							v-if="icon && iconType === 'image'">
							<!-- <i class="material-icons">shopping_cart</i> -->
						</span>
						<svg
							class="feather mr-2"
							:style="{ color: iconColor }"
							v-if="icon && iconType === 'feather'">
							<use :xlink:href="`/icons/feather-sprite.svg#${icon}`"/>
						</svg>
						<i v-if="icon && iconType === 'uicons'" :style="{ color: iconColor }" class="icon overview__icon" :class="fiClass"></i>
						<span class="overview__text">{{ text }}</span>
						<i class="overview__info material-icons info" v-tooltip="info" v-if="!!info">info</i>
					</div>
				</div>
				<div class="col-md-4 p-0 m-0 pl-1" v-if="isCanRedirected && isAdmin">
					<router-link :to="redirectLink" v-if="!isFetching">
						<span class="text-primary redirect">View all</span>
					</router-link>
				</div>
			</div>
			<div class="overview__text_sub" v-if="withAdditional && additionalInformation !== ''">{{ additionalInformation }}</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
	name: 'Overview',
	props: {
		type: {},
		number: {},
		prevNumber: {
			type: Number,
			default: null,
		},
		statsEnabled: {
			type: Boolean,
			default: () => false,
		},
		icon: {},
		iconType: {
			type: String,
			default: 'image', // image / feather
		},
		iconColor: {
			type: String,
			default: '', // image / feather
		},
		text: {},
		stats: {},
		info: {},
		size: {
			type: String,
			default: 'normal',
		},
		clickable: {
			type: Boolean,
			default: () => false,
		},
		withAdditional: {
			type: Boolean,
			default: () => true,
		},
		isFetching: {
			type: Boolean,
			default: false,
		},
		isCanRedirected: {
			type: Boolean,
			default: false,
		},
		redirectLink: {
			type: String,
			default: 'normal',
		},
	},
	methods: {
		onClick() {
			if (!this.isFetching) {
				if (this.clickable) {
					this.$emit('click');
				}
			}
		},
	},
	computed: {
		...mapGetters({
			isAdmin: 'isAdmin',
		}),
		iconClass() {
			return `icon-${this.icon}`;
		},
		fiClass() {
			return `fi fi-rr-${this.icon}`;
		},
		numberClass() {
			return `overview__number--${this.type}`;
		},
		numberDisplay() {
			const number = this.number ? this.number : 0;
			let display = number;
			if (this.type === 'price') {
				display = `Rp ${this.formatNumberPlain(number)}`;
			} else if (this.type === 'percentage') {
				display = `${this.formatNumberPlain(number)}%`;
			} else if (this.type === 'average_order') {
				display = `${number}x`;
			} else {
				display = this.formatNumberPlain(number);
			}
			return display;
		},
		hasPrevNumber() {
			return this.prevNumber !== null;
		},
		prevNumberDisplay() {
			const number = this.number ? this.number : 0;
			const prevNumber = this.prevNumber ? this.prevNumber : 0;

			let percentage = 0;
			let changes = 'stagnant'; // stagnant, increased, decreased
			let textColor = '';

			const differences = number - prevNumber;
			if (number === 0 && prevNumber === 0) {
				percentage = '0%'; // 0 divided by 0 is undefined
			} else if (prevNumber === 0) {
				percentage = ''; // x > 0 divided by 0 is infinity
			} else {
				percentage = `${Math.floor((differences / prevNumber) * 100)}%`;
			}

			if (differences < 0) {
				changes = 'decreased';
			} else if (differences === 0) {
				changes = 'stagnant';
			} else {
				percentage = percentage !== '' ? `+${percentage}` : percentage;
				changes = 'increased';
			}

			if (changes === 'increased') {
				textColor = 'text-success';
			} else if (changes === 'decreased') {
				textColor = 'text-danger';
			}

			if (this.text === 'Return') {
				if (changes === 'increased') {
					textColor = 'text-danger';
				} else if (changes === 'decreased') {
					textColor = 'text-success';
				}
			}

			return {
				percentage,
				textColor,
			};
		},
		additionalInformation() {
			let additionalInformation = '';
			if (this.stats) {
				if (this.text === 'Users') {
					additionalInformation = (this.stats.premium_users / this.stats.users) * 100;
					additionalInformation = `${Math.round(additionalInformation)}% Premium Users (${this.stats.premium_users})`;
				} else if (this.text === 'Products') {
					additionalInformation = '-';
				} else if (this.text === 'Orders') {
					additionalInformation = (this.stats.transaction / this.stats.submissions) * 100;
					additionalInformation = `${Math.round(additionalInformation)}% Orders Completed (${this.formatNumberPlain(this.stats.transaction)})`;
				} else if (this.text === 'Net Revenue') {
					const avgProductPrice = Math.round(this.stats.net_revenue / this.stats.transaction);
					additionalInformation = `Avg. product price Rp${this.formatNumberPlain(avgProductPrice)}`;
				} else if (this.text === 'Gross Revenue') {
					const avgRevenuePerUser = Math.round(this.stats.gross_revenue / this.stats.premium_users);
					additionalInformation = `Avg. revenue per user Rp${this.formatNumberPlain(avgRevenuePerUser)}`;
				} else if (this.text === 'Bump Revenue') {
					additionalInformation = '-';
				} else if (this.text === 'Shipping Cost') {
					const avgShippingCost = Math.round(this.stats.shipping_cost / this.stats.transaction);
					additionalInformation = `Avg. shipping cost Rp${this.formatNumberPlain(avgShippingCost)}`;
				} else if (this.text === 'Coupons Claimed') {
					additionalInformation = '-';
				}
			}
			return additionalInformation;
		},
		isFetchingstatistic() {
			return this.isFetching;
		},
	},
};
</script>

<style scoped>
.text-ellipsis {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.spinner-border {
    display: inline-block;
    width: 20px;
    height: 20px;
    vertical-align: -0.125em;
    border: 3px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    -webkit-animation: 0.75s linear infinite spinner-border;
    animation: 0.75s linear infinite spinner-border;
}
</style>
